import React, { useEffect, useState } from 'react';

import { TextileOrderForCheckout } from 'api/textile_deals/fetchTextileOrders';
import ButtonBlock from 'blocks/ButtonBlock';
import CardBlock from 'blocks/CardBlock';
import CheckboxBlock from 'blocks/CheckboxBlock';
import {
  EveryCard,
  EveryCardBody,
  EveryCardHeadline,
  EveryCardPadding
} from 'blocks/EveryCard/EveryCard';
import ItemStackBlock from 'blocks/ItemStackBlock';
import PaddingBlock from 'blocks/PaddingBlock';
import SliderBlock from 'blocks/SliderBlock';
import WideBlock from 'blocks/WideBlock';
import IconComponent from 'components/IconComponent';
import LoadingIndicatorComponent from 'components/LoadingIndicatorComponent';
import PageHeader from 'components/PageHeader/PageHeader';
import Paragraph from 'components/Paragraph/Paragraph';
import TextElement from 'components/TextElement/TextElement';
import InputMessage from 'elements/InputMessage';
import UppercaseHeading from 'elements/UppercaseHeading';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { textileOrderRoute } from 'utils/history/textile-order-route';
import { FormType } from 'utils/hooks/useForm';

const PreviewImage = styled.img`
  width: 80%;
`;
interface TextilePrintingScreenProps {
  form: FormType;
  updatePrinting: () => void;
  generatePrint: () => void;
  checkForPreview: () => void;
  textileOrder: TextileOrderForCheckout;
  studentFrontPreview?: string;
  studentBackPreview?: string;
  studentFrontMockup?: string;
  studentBackMockup?: string;
}

const TextilePrintingScreen = ({
  form,
  updatePrinting,
  generatePrint,
  checkForPreview,
  textileOrder,
  studentFrontPreview,
  studentBackPreview,
  studentFrontMockup,
  studentBackMockup
}: TextilePrintingScreenProps) => {
  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <PageHeader
        headline={<FormattedMessage id="Checkout print header" />}
        text={<FormattedMessage id="Checkout print info" />}
      />

      <Link
        to={textileOrderRoute(
          undefined,
          '/checkout/not_printed',
          textileOrder.id
        )}
      >
        <CardBlock>
          <PaddingBlock arrow={true}>
            <TextElement color="BLACK">
              <FormattedMessage id="textile checkout sources of errors" />
            </TextElement>
          </PaddingBlock>
        </CardBlock>
      </Link>

      {textileOrder.print_state === 'generated' && (
        <ItemStackBlock gap="M">
          <WideBlock padded={true}>
            <ItemStackBlock gap="S">
              <UppercaseHeading>
                <FormattedMessage id="Design preview" />
              </UppercaseHeading>

              <SliderBlock>
                <PreviewImage src={studentFrontMockup} alt="Placeholder" />
                <PreviewImage src={studentBackMockup} alt="Placeholder" />
              </SliderBlock>
            </ItemStackBlock>
          </WideBlock>

          <>
            <a
              href={studentFrontPreview}
              download="nuggit-vorschau-vorne.jpg"
              target="_blank"
              rel="noreferrer noopener"
            >
              <EveryCard>
                <EveryCardPadding>
                  <EveryCardBody>
                    <EveryCardHeadline>
                      <FormattedMessage id="textile checkout check print front" />
                    </EveryCardHeadline>
                  </EveryCardBody>
                  <IconComponent icon="DOWNLOAD" fill="BLACK" size={2} />
                </EveryCardPadding>
              </EveryCard>
            </a>
          </>

          <>
            <a
              href={studentBackPreview}
              download="nuggit-vorschau-hinten.jpg"
              target="_blank"
              rel="noreferrer noopener"
            >
              <EveryCard>
                <EveryCardPadding>
                  <EveryCardBody>
                    <EveryCardHeadline>
                      <FormattedMessage id="textile checkout check print back" />
                    </EveryCardHeadline>
                  </EveryCardBody>
                  <IconComponent icon="DOWNLOAD" fill="BLACK" size={2} />
                </EveryCardPadding>
              </EveryCard>
            </a>
          </>

          <CheckboxBlock>
            <CheckboxBlock.CheckboxElement
              id="accept_print"
              {...form.bindCheckbox('accept_print')}
            />
            <CheckboxBlock.LabelElement htmlFor="accept_print">
              <FormattedMessage id="Checkout print Accept" />
            </CheckboxBlock.LabelElement>
          </CheckboxBlock>

          {form.values.accept_print ? (
            <ButtonBlock background="PRIMARY" onClick={() => updatePrinting()}>
              <FormattedMessage id="Save" />
            </ButtonBlock>
          ) : (
            <ButtonBlock
              background="GRAY950"
              color="GRAY800"
              disabled={true}
              onClick={() => updatePrinting()}
            >
              <FormattedMessage id="textile checkout print missing" />
            </ButtonBlock>
          )}
        </ItemStackBlock>
      )}

      {(textileOrder.print_state === 'print_open' ||
        textileOrder.print_state === 'generated') && (
        <ButtonBlock
          background={
            textileOrder.print_state === 'print_open'
              ? 'PRIMARY'
              : 'PRIMARY_LIGHT'
          }
          color={
            textileOrder.print_state === 'generated' ? 'PRIMARY_DARK' : 'WHITE'
          }
          onClick={() => generatePrint()}
        >
          <FormattedMessage id="Generate print textile" />
        </ButtonBlock>
      )}

      {(textileOrder.print_state === 'generating' ||
        textileOrder.print_state === 'enqueued') && (
        <>
          {showSpinner ? (
            <LoadingIndicatorComponent />
          ) : (
            <ButtonBlock
              background="SECONDARY"
              onClick={() => {
                checkForPreview();
                setTimeout(() => {
                  setShowSpinner(true);
                  setTimeout(() => setShowSpinner(false), 1000); // Hides the spinner after 1 second
                }, 200);
              }}
            >
              <FormattedMessage id="Reload" />
            </ButtonBlock>
          )}

          <InputMessage warning={true}>
            <Paragraph>
              <FormattedMessage id="textile print is generating" />
            </Paragraph>
          </InputMessage>
        </>
      )}
    </>
  );
};
export default TextilePrintingScreen;
